/**
 * First-touch acquisition attribution (T22).
 *
 * Remembers HOW a visitor arrived — the UTM parameters of the link they
 * followed, or the external site that sent them — so that if they register
 * within 30 days the account carries the channel. The admin funnel report
 * reads it from `users.acquisition`.
 *
 * **First touch wins.** While a valid entry exists nothing overwrites it: the
 * ad that brought someone is credited even if they come back through the
 * homepage the next week.
 *
 * **Everything is wrapped in try/catch.** localStorage throws in private
 * windows, with blocked site data and in some embedded browsers, and
 * attribution is a convenience that must never break a page.
 *
 * Called once from `index.js`, BEFORE the app renders and not from an effect:
 * child effects run before a parent's, so a `<Navigate>` in the first route
 * (ProtectedRoute, a redirect page) could rewrite the URL before an effect in
 * App ever read it.
 */

export const ACQUISITION_KEY = "fd_acq";
export const ACQUISITION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const UTM_FIELDS = ["source", "medium", "campaign", "content", "term"];

/** Hostnames compared without a leading `www.`, so the apex and `www` — which
 *  301 to each other — never count as an external referrer. */
function bareHost(hostname) {
  return String(hostname || "").toLowerCase().replace(/^www\./, "");
}

function isExternalReferrer(referrer) {
  if (!referrer) return false;
  try {
    return bareHost(new URL(referrer).hostname) !== bareHost(window.location.hostname);
  } catch (e) {
    return false;
  }
}

/** The stored entry, if it is still inside its 30 days. An expired or
 *  unreadable one is removed. */
export function getAcquisition() {
  try {
    const raw = window.localStorage.getItem(ACQUISITION_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    const capturedAt = Date.parse(entry && entry.captured_at);
    if (!entry || typeof entry !== "object" || Number.isNaN(capturedAt)
        || Date.now() - capturedAt >= ACQUISITION_TTL_MS) {
      window.localStorage.removeItem(ACQUISITION_KEY);
      return null;
    }
    return entry;
  } catch (e) {
    // Unparseable (or storage unreadable): drop it so it cannot linger.
    clearAcquisition();
    return null;
  }
}

export function clearAcquisition() {
  try {
    window.localStorage.removeItem(ACQUISITION_KEY);
  } catch (e) { /* nothing to clear */ }
}

/** Store the visit's attribution unless a valid entry already exists. */
export function captureAcquisition() {
  try {
    if (getAcquisition()) return;

    const params = new URLSearchParams(window.location.search);
    const utm = {};
    UTM_FIELDS.forEach((field) => {
      utm[field] = (params.get(`utm_${field}`) || "").trim();
    });

    const base = {
      referrer: document.referrer || "",
      // The path only: the query string can carry a reset or verification
      // token, and the UTMs are already stored on their own above.
      landing_path: window.location.pathname,
      captured_at: new Date().toISOString(),
    };

    let entry = null;
    if (utm.source) {
      entry = { ...utm, ...base };
    } else if (isExternalReferrer(document.referrer)) {
      entry = base;
    }
    if (entry) window.localStorage.setItem(ACQUISITION_KEY, JSON.stringify(entry));
  } catch (e) { /* attribution is optional */ }
}
